import React, { useState } from 'react';
import { Tooltip, Box, Typography, CircularProgress, Stack } from '@mui/material';
import { api } from '../../lib/api';
import { safeConsole } from '../../lib/piiProtection';

interface AuditLogEntry {
  id: string;
  fieldName: string;
  oldValue?: string;
  newValue?: string;
  editedByName: string;
  editedAt: string;
  editReasonCategory?: string;
  editReasonDetail?: string;
}

interface VitalsAuditTooltipProps {
  visitId: string;
  fieldName: string;
  label: string;
  children: React.ReactNode;
}

export function VitalsAuditTooltip({ visitId, fieldName, children }: VitalsAuditTooltipProps) {
  const [open, setOpen] = useState(false);
  const [auditHistory, setAuditHistory] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(false);

  const handleMouseEnter = async () => {
    setOpen(true);
    if (auditHistory.length === 0) {
      setLoading(true);
      try {
        const response = await api.visits.getFieldHistory(visitId, fieldName);
        if (response.data) {
          setAuditHistory(response.data);
        }
      } catch (err) {
        safeConsole.error('Failed to load audit history:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleMouseLeave = () => {
    setOpen(false);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatReasonCategory = (category?: string) => {
    if (!category) return '';
    return category
      .split('_')
      .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ');
  };

  const tooltipContent = (
    <Box sx={{ maxWidth: 300 }}>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
          <CircularProgress size={20} />
        </Box>
      ) : auditHistory.length === 0 ? (
        <Typography variant="body2">No changes recorded</Typography>
      ) : (
        <Stack spacing={1}>
          {auditHistory.slice(0, 3).map((entry, idx) => (
            <Box key={entry.id} sx={{ pb: 1, borderBottom: idx < 2 ? '1px solid rgba(255,255,255,0.2)' : 'none' }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {formatDate(entry.editedAt)}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.7)' }}>
                by {entry.editedByName}
              </Typography>

              {entry.oldValue !== undefined && entry.newValue !== undefined && (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'rgba(255,255,255,0.8)' }}>
                  {entry.oldValue} → {entry.newValue}
                </Typography>
              )}

              {entry.editReasonCategory && (
                <Box sx={{ mt: 0.5, fontSize: '0.65rem' }}>
                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 500 }}>
                    {formatReasonCategory(entry.editReasonCategory)}
                  </Typography>
                  {entry.editReasonDetail && (
                    <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.6)' }}>
                      {entry.editReasonDetail.substring(0, 100)}
                      {entry.editReasonDetail.length > 100 ? '...' : ''}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          ))}

          {auditHistory.length > 3 && (
            <Typography variant="caption" sx={{ pt: 1, color: 'rgba(255,255,255,0.5)', fontStyle: 'italic' }}>
              +{auditHistory.length - 3} more changes
            </Typography>
          )}
        </Stack>
      )}
    </Box>
  );

  return (
    <Tooltip
      title={tooltipContent}
      arrow
      open={open}
      onOpen={handleMouseEnter}
      onClose={handleMouseLeave}
      placement="top"
      disableInteractive={loading || auditHistory.length === 0}
      sx={{
        '& .MuiTooltip-tooltip': {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          color: 'rgba(255, 255, 255, 0.95)',
          fontSize: '0.75rem',
          maxWidth: '300px',
          padding: '12px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '6px',
        },
      }}
    >
      <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        {children}
      </div>
    </Tooltip>
  );
}

export default VitalsAuditTooltip;
